import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { register } from '../core/api/auth-service';
import { classifyIdentifier } from '../core/auth/sign-in-identifier';
import { describeApiError } from '../core/errors/describe-api-error';
import { describeFieldErrors } from '../core/errors/describe-field-errors';
import type { TranslationKey } from '../core/i18n/translation';
import { useLanguage } from '../core/hooks/use-language';
import {
    FORMATTED_LENGTH,
    KAZAKHSTAN_DIAL_CODE,
    formatSubscriberDigits,
    isCompleteSubscriber,
    toE164,
    toSubscriberDigits
} from '../core/phone/kazakhstan-phone';
import { useTranslation } from '../core/hooks/use-translation';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { AuthSurface } from './auth/auth-surface';
import { TextField } from './auth/text-field';

/** Repeated from the server's Identity options so the box can say so before it is posted. */
const MINIMUM_PASSWORD_LENGTH = 8;

/**
 * Public registration.
 *
 * **A telephone number is the only identifier this asks for.** It is what the account is reachable
 * by, and requiring an address as well would turn away the customers who deal with the company by
 * telephone — which is most of them. It is collected as ten digits under a fixed `+7`, because the
 * server will only open an account on a number it can classify as a Kazakhstan one. The IIN box is here because a customer who gives one now never
 * has to be chased for it later, and it is marked optional because a case can be opened without it.
 *
 * The account this creates carries no standing with the company: registering opens no case and
 * assigns nobody. That is what keeps the form from being a way for a stranger to create work.
 */
export function RegisterPage() {
    const { t } = useTranslation();
    const language = useLanguage();
    const navigate = useNavigate();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    /** The ten digits after `+7`. The country code is the field's addon, never part of the value. */
    const [phone, setPhone] = useState('');
    const [iin, setIin] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errorKey, setErrorKey] = useState<TranslationKey | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, TranslationKey>>({});
    const [submitting, setSubmitting] = useState(false);

    async function onSubmit(event: FormEvent) {
        event.preventDefault();
        setErrorKey(null);

        const problems = validate();

        // Checked here as well as on the server, so a mistyped number is corrected in the field it
        // was typed in rather than coming back as a rejected form. The server decides either way;
        // this only reads the shape of the text and looks nothing up.
        if (Object.keys(problems).length > 0) {
            setFieldErrors(problems);
            return;
        }

        setFieldErrors({});
        setSubmitting(true);

        try {
            await register({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phone: toE164(phone),
                iin: blankToNull(iin),
                email: blankToNull(email),
                password,
                preferredLanguage: language
            });

            // Straight into the portal: the server opened the session, so there is nothing left to
            // sign in with.
            void navigate(`/${language}/portal`, { replace: true });
        } catch (error) {
            // The banner gives way to the fields when the server named them: repeating the
            // complaint above a form that is now marking the box reads as two separate faults.
            const fields = describeFieldErrors(error);

            setFieldErrors(fields);
            setErrorKey(Object.keys(fields).length > 0 ? null : describeApiError(error));
        } finally {
            setSubmitting(false);
        }
    }

    function validate(): Record<string, TranslationKey> {
        const problems: Record<string, TranslationKey> = {};

        if (!isCompleteSubscriber(phone)) {
            problems['phone'] = 'auth.register.phone_invalid';
        }

        // Blank is a complete answer for both of these, so each is only judged when it holds
        // something. The IIN goes through the same reader the sign-in box uses, which is what makes
        // a number accepted here one the customer could later sign in with.
        if (iin.trim() !== '' && classifyIdentifier(iin) !== 'iin') {
            problems['iin'] = 'auth.register.iin_invalid';
        }

        if (email.trim() !== '' && classifyIdentifier(email) !== 'email') {
            problems['email'] = 'validation.email';
        }

        if (password.length < MINIMUM_PASSWORD_LENGTH) {
            problems['password'] = 'auth.activate.too_short';
        }

        // The server never sees this box. Two password fields exist so a typo is caught before it
        // becomes an account nobody can sign in to, and only this page can compare them.
        if (confirmPassword !== password) {
            problems['confirmPassword'] = 'auth.activate.mismatch';
        }

        return problems;
    }

    return (
        <AuthSurface>
            <h1 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100">
                {t('auth.register.title')}
            </h1>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{t('auth.register.subtitle')}</p>

            <form className="mt-7 space-y-4" onSubmit={(event) => void onSubmit(event)}>
                {/* Two short boxes that are always filled together, side by side rather than
                    stacked: seven full-width fields in a column is a form people abandon. */}
                <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                        label={t('auth.register.first_name')}
                        type="text"
                        value={firstName}
                        autoComplete="given-name"
                        error={fieldErrors['firstName']}
                        onChange={setFirstName}
                    />
                    <TextField
                        label={t('auth.register.last_name')}
                        type="text"
                        value={lastName}
                        autoComplete="family-name"
                        error={fieldErrors['lastName']}
                        onChange={setLastName}
                    />
                </div>
                <TextField
                    label={t('auth.register.phone')}
                    type="tel"
                    value={formatSubscriberDigits(phone)}
                    autoComplete="tel-national"
                    inputMode="tel"
                    prefix={KAZAKHSTAN_DIAL_CODE}
                    placeholder={t('booking.s3.phone_ph')}
                    maxLength={FORMATTED_LENGTH}
                    error={fieldErrors['phone']}
                    hint={t('auth.register.phone_hint')}
                    onChange={(value) => setPhone(toSubscriberDigits(value))}
                />
                <TextField
                    label={t('auth.register.iin')}
                    type="text"
                    value={iin}
                    autoComplete="off"
                    inputMode="numeric"
                    required={false}
                    error={fieldErrors['iin']}
                    hint={t('auth.register.iin_hint')}
                    onChange={setIin}
                />
                <TextField
                    label={t('auth.register.email')}
                    type="email"
                    value={email}
                    autoComplete="email"
                    inputMode="email"
                    required={false}
                    error={fieldErrors['email']}
                    hint={t('auth.register.email_hint')}
                    onChange={setEmail}
                />
                <TextField
                    label={t('auth.register.password')}
                    type="password"
                    value={password}
                    autoComplete="new-password"
                    error={fieldErrors['password']}
                    hint={t('auth.activate.too_short')}
                    onChange={setPassword}
                />
                <TextField
                    label={t('auth.register.confirm_password')}
                    type="password"
                    value={confirmPassword}
                    autoComplete="new-password"
                    error={fieldErrors['confirmPassword']}
                    onChange={setConfirmPassword}
                />

                {errorKey !== null && <Alert>{t(errorKey)}</Alert>}

                <Button type="submit" size="lg" block disabled={submitting}>
                    {submitting ? t('common.loading') : t('auth.register.submit')}
                </Button>
            </form>

            <p className="mt-6 border-t border-slate-200 pt-5 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
                {t('auth.register.have_account')}{' '}
                <Link
                    className="font-semibold text-brand-700 hover:underline dark:text-brand-400"
                    to={`/${language}/account/sign-in`}
                >
                    {t('auth.sign_in.submit')}
                </Link>
            </p>
        </AuthSurface>
    );
}

function blankToNull(value: string): string | null {
    return value.trim() === '' ? null : value.trim();
}
